import type { HabitCategory, HabitWeekReport } from '@contracts';
import { HeartPulse, Sparkles, BookOpen, CircleEllipsis } from '@ui/shared/icons';
import { TrendSparkline } from '@ui/desktop/weekly-review/TrendSparkline';

const CATEGORY_ICON: Record<HabitCategory, typeof HeartPulse> = {
  health: HeartPulse,
  self_care: Sparkles,
  study: BookOpen,
  other: CircleEllipsis,
};

const CATEGORY_TINT_VAR: Record<HabitCategory, string> = {
  health: 'var(--accent-health)',
  self_care: 'var(--accent-selfcare)',
  study: 'var(--accent-study)',
  other: 'var(--accent-other)',
};

/**
 * `HabitReviewCard` (desktop-weekly-review.md §9.3) — replaces the 6-column
 * `HabitsTable`/table-row on phone. This is the one panel that gets a
 * genuine structural redesign, not a resize: a 6-column table has no honest
 * narrow-width equivalent (habit / planned / actual / minutes / trend /
 * status crammed into ~358px recreates exactly the bug this pass fixes).
 * Each habit becomes its own card (`elevation.1`, matching `NextUpCard`'s
 * phone card weight): row 1 identity + status, row 2 planned-vs-actual as
 * one sentence, row 3 trend + study minutes. Tap-to-expand detail strip
 * carries over unchanged behavior from the desktop table row.
 */
export function HabitReviewCard({
  habit,
  expanded,
  onToggle,
}: {
  readonly habit: HabitWeekReport;
  readonly expanded: boolean;
  readonly onToggle: () => void;
}) {
  const Icon = CATEGORY_ICON[habit.category];
  const statusLabel = habit.current.met ? 'Met' : habit.habitKind === 'build' ? 'Under plan' : 'Over allowance';
  const plannedLabel = formatPlanned(habit);
  const actualLabel = formatActual(habit);
  const hasTrendOrStudy = habit.trend.length > 0 || habit.category === 'study';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      aria-expanded={expanded}
      aria-label={`${habit.name}, ${statusLabel}`}
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--layout-card-padding-phone)',
        cursor: 'pointer',
      }}
    >
      {/* Row 1: identity + status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <span
          aria-hidden="true"
          style={{
            width: '28px',
            height: '28px',
            borderRadius: 'var(--radius-sm)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `color-mix(in srgb, ${CATEGORY_TINT_VAR[habit.category]} 14%, transparent)`,
            flex: 'none',
          }}
        >
          <Icon size={16} color={CATEGORY_TINT_VAR[habit.category]} aria-hidden="true" />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 'var(--type-body-size)', fontWeight: 500, color: 'var(--text-primary)' }}>{habit.name}</div>
          {habit.habitKind === 'avoid' && (
            <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>keeping in check</div>
          )}
        </div>
        <span
          style={{
            fontSize: 'var(--type-caption-size)',
            fontWeight: 500,
            color: habit.current.met ? 'var(--hue-primary)' : 'var(--text-secondary)',
            flex: 'none',
          }}
        >
          {statusLabel}
        </span>
      </div>

      {/* Row 2: planned-vs-actual, one sentence */}
      <div className="num" style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-body-size)' }}>
        <span style={{ color: 'var(--text-secondary)' }}>{plannedLabel} planned</span>
        <span style={{ color: 'var(--text-muted)' }}> · </span>
        <span style={{ color: 'var(--text-primary)' }}>{actualLabel}</span>
      </div>

      {/* Row 3: trend + study minutes */}
      {hasTrendOrStudy && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'var(--space-2)' }}>
          <StudyMinutesLabel habit={habit} />
          <div style={{ marginLeft: 'auto' }}>
            <TrendSparkline trend={habit.trend} category={habit.category} />
          </div>
        </div>
      )}

      {expanded && (
        <div
          style={{
            background: 'var(--surface-sunken)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-3)',
            marginTop: 'var(--space-3)',
            fontSize: 'var(--type-caption-size)',
            color: 'var(--text-secondary)',
          }}
        >
          {habit.current.actual} logged this week against a plan of {habit.current.planned}.
        </div>
      )}
    </div>
  );
}

function StudyMinutesLabel({ habit }: { readonly habit: HabitWeekReport }) {
  if (habit.category !== 'study') return <span />;
  return (
    <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>Not tracked yet</span>
  );
}

function formatPlanned(habit: HabitWeekReport): string {
  if (habit.habitKind === 'build') return `${habit.current.planned}×`;
  return `≤ ${habit.current.planned}`;
}

function formatActual(habit: HabitWeekReport): string {
  if (habit.habitKind === 'build') return `${habit.current.actual}× done`;
  return `${habit.current.actual} logged`;
}
